/**
 * Yönetici Maliyet sayfası — BOSS MaliyetPage'in şube filtreli kopyası.
 * Sadece yöneticinin atandığı şubelerdeki personelin maliyeti gösterilir.
 */

import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import {
  Coins, Users, Activity, ChevronLeft, ChevronRight, Clock,
  TrendingUp, Calculator, MoonStar,
} from 'lucide-react'
import {
  tumAktifPersonellerinMaliyeti,
  aktifMesaiMaliyeti,
  type ToplamMaliyetOzeti,
  type AktifMaliyetOzeti,
} from '@/lib/maas/toplam-maliyet'
import { tumSubeleriGetir } from '@/lib/services/sube-service'
import { useAuth } from '@/contexts/AuthContext'
import type { Sube, KullaniciPersonel } from '@/types'

function buAyStr(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function ayEtiketi(ay: string): string {
  const [y, m] = ay.split('-').map(Number)
  if (!y || !m) return ay
  const aylar = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
  ]
  return `${aylar[m - 1]} ${y}`
}

function paraTam(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

function paraKurus(n: number): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function YoneticiMaliyetPage() {
  const { kullanici } = useAuth()
  const benimSubeIdleri = useMemo<string[] | null>(() => {
    if (kullanici?.rol === 'boss') return null
    if (kullanici?.rol !== 'personel') return []
    return (kullanici as KullaniciPersonel).subeIdleri ?? []
  }, [kullanici])

  const [donem, setDonem] = useState<string>(buAyStr())
  const [ozet, setOzet] = useState<ToplamMaliyetOzeti | null>(null)
  const [aktifMaliyet, setAktifMaliyet] = useState<AktifMaliyetOzeti | null>(null)
  const [subeler, setSubeler] = useState<Map<string, Sube>>(new Map())
  const [yukleniyor, setYukleniyor] = useState(true)
  const [hata, setHata] = useState<string | null>(null)

  const baseToplamAnlikRef = useRef<number>(0)
  const basePersonelAnlikRef = useRef<Map<string, number>>(new Map())
  const baseTimestampRef = useRef<number>(Date.now())
  const [yansitilanToplam, setYansitilanToplam] = useState<number>(0)
  const [yansitilanSatirlar, setYansitilanSatirlar] = useState<Map<string, number>>(new Map())

  const isBuAy = donem === buAyStr()

  function subeFiltrele<T extends { personel: { subeIdleri: string[] } }>(satirlar: T[]): T[] {
    if (benimSubeIdleri === null) return satirlar
    return satirlar.filter(s =>
      s.personel.subeIdleri.some(id => benimSubeIdleri.includes(id))
    )
  }

  function aktifMaliyetFiltrele(m: AktifMaliyetOzeti, subeIdSet: Set<string>): AktifMaliyetOzeti {
    if (benimSubeIdleri === null) return m
    const detay = m.detay.filter(d => subeIdSet.has(d.personelId))
    const saatlikToplam = detay.reduce((s, d) => s + d.saatlik, 0)
    return {
      ...m,
      detay,
      saatlikToplam,
      aktifPersonelSayisi: detay.length,
    }
  }

  const tumYukle = useCallback(async () => {
    try {
      setYukleniyor(true)
      const [o, m, s] = await Promise.all([
        tumAktifPersonellerinMaliyeti(donem),
        aktifMesaiMaliyeti(),
        tumSubeleriGetir(),
      ])
      const filtreliSatirlar = subeFiltrele(o.satirlar)
      const filtreliPersonelIdSet = new Set(filtreliSatirlar.map(x => x.personel.id))
      const filtreliAktif = aktifMaliyetFiltrele(m, filtreliPersonelIdSet)
      const toplamAnlik = filtreliSatirlar.reduce((s, x) => s + x.anlikMaliyet, 0)
      const toplamCalisma = filtreliSatirlar.reduce((s, x) => s + x.hesap.toplamSaat, 0)
      const toplamPuantaj = filtreliSatirlar.reduce((s, x) => s + x.hesap.puantajToplam, 0)
      const toplamAvans = filtreliSatirlar.reduce((s, x) => s + x.hesap.odenmisAvansToplam, 0)
      const toplamNet = filtreliSatirlar.reduce((s, x) => s + x.hesap.netMaas, 0)

      const filtreliOzet: ToplamMaliyetOzeti = {
        ...o,
        satirlar: filtreliSatirlar,
        personelSayisi: filtreliSatirlar.length,
        toplamAnlikMaliyet: toplamAnlik,
        toplamCalismaSaat: toplamCalisma,
        toplamPuantaj,
        toplamOdenmisAvans: toplamAvans,
        toplamNetMaas: toplamNet,
      }

      setOzet(filtreliOzet)
      setAktifMaliyet(filtreliAktif)
      const sm = new Map<string, Sube>()
      s.forEach((x) => {
        if (benimSubeIdleri === null || benimSubeIdleri.includes(x.id)) sm.set(x.id, x)
      })
      setSubeler(sm)

      baseToplamAnlikRef.current = toplamAnlik
      const personelAnlik = new Map<string, number>()
      filtreliSatirlar.forEach((s) => personelAnlik.set(s.personel.id, s.anlikMaliyet))
      basePersonelAnlikRef.current = personelAnlik
      baseTimestampRef.current = Date.now()
      setYansitilanToplam(toplamAnlik)
      setYansitilanSatirlar(new Map(personelAnlik))

      setHata(null)
    } catch (err) {
      console.error(err)
      setHata('Hesaplanamadı: ' + (err as Error).message)
    } finally {
      setYukleniyor(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donem, benimSubeIdleri])

  useEffect(() => {
    tumYukle()
  }, [tumYukle])

  useEffect(() => {
    if (!isBuAy) return
    const interval = setInterval(() => { tumYukle() }, 30000)
    return () => clearInterval(interval)
  }, [tumYukle, isBuAy])

  useEffect(() => {
    if (!isBuAy || !aktifMaliyet || !ozet) return
    if (aktifMaliyet.saatlikToplam === 0) {
      setYansitilanToplam(baseToplamAnlikRef.current)
      setYansitilanSatirlar(new Map(basePersonelAnlikRef.current))
      return
    }
    const interval = setInterval(() => {
      const gecenSn = (Date.now() - baseTimestampRef.current) / 1000
      const toplamBirikim = (aktifMaliyet.saatlikToplam / 3600) * gecenSn
      setYansitilanToplam(baseToplamAnlikRef.current + toplamBirikim)
      const yeniSatirlar = new Map(basePersonelAnlikRef.current)
      aktifMaliyet.detay.forEach(({ personelId, saatlik }) => {
        const baseAnlik = yeniSatirlar.get(personelId) ?? 0
        const birikim = (saatlik / 3600) * gecenSn
        yeniSatirlar.set(personelId, baseAnlik + birikim)
      })
      setYansitilanSatirlar(yeniSatirlar)
    }, 200)
    return () => clearInterval(interval)
  }, [aktifMaliyet, ozet, isBuAy])

  function ayDegistir(yon: -1 | 1) {
    const [y, m] = donem.split('-').map(Number)
    if (!y || !m) return
    const d = new Date(y, m - 1 + yon, 1)
    setDonem(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
  }

  const canliAkiyor = !!(isBuAy && aktifMaliyet && aktifMaliyet.saatlikToplam > 0)

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <Coins className="w-6 h-6 text-amber-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Maliyet</h1>
          <p className="text-sm text-gray-600 mt-0.5">
            Şubendeki personel anlık biriken maliyet — canlı
          </p>
        </div>
      </div>

      {aktifMaliyet && isBuAy && (
        <div className="card bg-gradient-to-br from-amber-500 to-orange-600 border-0 text-white mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-5 h-5 animate-pulse" />
            <h2 className="font-bold text-sm uppercase tracking-wide opacity-90">
              Şu Anki Saatlik Maliyet
            </h2>
          </div>
          <p className="text-4xl font-bold tabular-nums">
            {paraTam(aktifMaliyet.saatlikToplam)}
            <span className="text-2xl ml-1 opacity-80">₺/saat</span>
          </p>
          <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
            <div className="bg-white/15 rounded-lg px-2 py-1.5">
              <div className="opacity-75">Mesaide</div>
              <div className="font-semibold flex items-center gap-1">
                <Users className="w-3 h-3" />
                {aktifMaliyet.aktifPersonelSayisi} kişi
              </div>
            </div>
            <div className="bg-white/15 rounded-lg px-2 py-1.5">
              <div className="opacity-75">Saniyede</div>
              <div className="font-semibold tabular-nums">
                {paraKurus(aktifMaliyet.saatlikToplam / 3600)} ₺
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card mb-4">
        <div className="flex items-center gap-2 mb-3">
          <button onClick={() => ayDegistir(-1)} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 text-center font-semibold text-gray-900">
            {ayEtiketi(donem)}
          </div>
          <button onClick={() => ayDegistir(1)} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <ChevronRight className="w-5 h-5" />
          </button>
          <button
            onClick={() => setDonem(buAyStr())}
            className="px-3 py-1.5 text-xs text-brand-600 hover:underline whitespace-nowrap"
          >
            Bu ay
          </button>
        </div>

        {ozet && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
            <div className="bg-blue-50 rounded-lg p-2">
              <div className="text-xs text-blue-700">Personel</div>
              <div className="font-bold text-blue-900">{ozet.personelSayisi}</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-2">
              <div className="text-xs text-purple-700">Toplam Saat</div>
              <div className="font-bold text-purple-900">
                {Math.round(ozet.toplamCalismaSaat)}
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-2">
              <div className="text-xs text-green-700">Puantaj</div>
              <div className={`font-bold ${ozet.toplamPuantaj >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                {ozet.toplamPuantaj >= 0 ? '+' : ''}{paraTam(ozet.toplamPuantaj)} ₺
              </div>
            </div>
            <div className="bg-amber-50 rounded-lg p-2">
              <div className="text-xs text-amber-700">Avans</div>
              <div className="font-bold text-amber-900">
                {paraTam(ozet.toplamOdenmisAvans)} ₺
              </div>
            </div>
          </div>
        )}
      </div>

      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm mb-4">
          {hata}
        </div>
      )}

      {yukleniyor ? (
        <div className="card text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
          <p className="text-sm text-gray-600 mt-3">Hesaplanıyor...</p>
        </div>
      ) : !ozet || ozet.satirlar.length === 0 ? (
        <div className="card text-center py-12">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="font-semibold text-gray-700">Şubende aktif personel yok</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div className="card bg-gradient-to-br from-brand-500 to-indigo-600 border-0 text-white">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingUp className="w-4 h-4 opacity-90" />
                <p className="text-xs uppercase tracking-wide opacity-90">
                  {ayEtiketi(donem)} — Anlık Biriken
                </p>
              </div>
              <p className="text-3xl font-bold tabular-nums leading-tight">
                {canliAkiyor ? paraKurus(yansitilanToplam) : paraTam(ozet.toplamAnlikMaliyet)}
                <span className="text-xl ml-1 opacity-80">₺</span>
              </p>
              <p className="text-[11px] opacity-75 mt-1">
                Çalışılan saat × saatlik karşılık
                {canliAkiyor && (
                  <span className="ml-2 inline-flex items-center gap-1">
                    • <Activity className="w-2.5 h-2.5 animate-pulse" /> canlı
                  </span>
                )}
              </p>
            </div>

            <div className="card bg-gradient-to-br from-emerald-500 to-teal-600 border-0 text-white">
              <div className="flex items-center gap-1.5 mb-1">
                <Calculator className="w-4 h-4 opacity-90" />
                <p className="text-xs uppercase tracking-wide opacity-90">
                  {ayEtiketi(donem)} — Ay Sonu Tahmini
                </p>
              </div>
              <p className="text-3xl font-bold tabular-nums leading-tight">
                {paraTam(ozet.toplamNetMaas)}
                <span className="text-xl ml-1 opacity-80">₺</span>
              </p>
              <p className="text-[11px] opacity-75 mt-1">
                Net ödeme (puantaj/avans/ek-kesinti dahil)
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-gray-500 px-1">
              Personel Detayı (Anlık Maliyete Göre)
            </p>
            {ozet.satirlar.map(({ personel, hesap, saatlik }) => {
              const subeAdlari = personel.subeIdleri
                .map((id) => subeler.get(id)?.isim)
                .filter(Boolean) as string[]
              const aktifMi = aktifMaliyet?.detay.some((d) => d.personelId === personel.id) ?? false
              const yansitilanAnlik = yansitilanSatirlar.get(personel.id)
                ?? hesap.toplamSaat * saatlik

              return (
                <div key={personel.id} className="card">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-gray-900 truncate">{personel.isim}</h3>
                        {aktifMi ? (
                          <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full inline-flex items-center gap-0.5 font-semibold">
                            <Activity className="w-2.5 h-2.5 animate-pulse" />
                            Mesaide
                          </span>
                        ) : (
                          <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded-full inline-flex items-center gap-0.5">
                            <MoonStar className="w-2.5 h-2.5" />
                            Mesai Dışı
                          </span>
                        )}
                        <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                          {personel.maasTipi === 'aylik' ? 'Aylık' : 'Saatlik'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {subeAdlari.join(', ')}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-600 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {hesap.toplamSaat.toFixed(1)} sa
                        </span>
                        <span>×</span>
                        <span>{paraTam(saatlik)} ₺/sa</span>
                        {hesap.puantajToplam !== 0 && (
                          <span className={hesap.puantajToplam > 0 ? 'text-green-700' : 'text-red-700'}>
                            P: {hesap.puantajToplam > 0 ? '+' : ''}{paraTam(hesap.puantajToplam)} ₺
                          </span>
                        )}
                        {hesap.odenmisAvansToplam > 0 && (
                          <span className="text-amber-700">
                            A: −{paraTam(hesap.odenmisAvansToplam)} ₺
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-2xl font-bold text-gray-900 tabular-nums">
                        {aktifMi && isBuAy
                          ? paraKurus(yansitilanAnlik)
                          : paraTam(yansitilanAnlik)}
                      </p>
                      <p className="text-xs text-gray-500">₺ anlık</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        ay sonu net: {paraTam(hesap.netMaas)} ₺
                      </p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
